/**
 * プレイリストのサムネイル比率定義（タイル型・スライダー型 共通モジュール）。
 *
 * 管理画面（プレイリスト詳細）でユーザーが選択し、scenario.config.aspectRatio に
 * プレイリスト単位で保存する。埋め込み側（TilePlayer / SliderPlayer）と
 * 管理画面プレビューの両方で同じ値を参照する。
 *
 * 「選択肢リスト」と「変換ユーティリティ」だけを共通化しており、選択された値そのものは
 * 各プレイリストの config に個別保存されるため、タイル型A=1:1 / スライダー型B=4:3 のように
 * プレイリストごとに独立して設定できる。
 */
export const TILE_ASPECT_RATIOS = ['1:1', '3:4', '9:16', '4:3'] as const

/**
 * ワイド型（WIDE）のインラインプレイヤーの比率。
 *
 * タイル／スライダーの選択肢（`TILE_ASPECT_RATIOS`）には**入れない**。
 * ワイド型は横型16:9固定で、ユーザーが選ぶ設定ではないため。
 */
export const WIDE_ASPECT_RATIO = '16 / 9'

export type TileAspectRatio = (typeof TILE_ASPECT_RATIOS)[number]

/** タイル型の既定値（後方互換のため正方形）。 */
export const DEFAULT_TILE_ASPECT_RATIO: TileAspectRatio = '1:1'

/** スライダー型の既定値（後方互換のため縦長 9:16）。 */
export const DEFAULT_SLIDER_ASPECT_RATIO: TileAspectRatio = '9:16'

export const TILE_ASPECT_RATIO_LABELS: Record<TileAspectRatio, string> = {
  '1:1': '正方形 (1:1)',
  '3:4': '縦長 (3:4)',
  '9:16': '縦長 (9:16)',
  '4:3': '横長 (4:3)',
}

/**
 * 不正な値（旧データや手動編集された JSON）から安全な既定値へフォールバックする。
 * fallback を差し替えることでタイル／スライダーそれぞれの既定値に対応する。
 */
export function normalizeAspectRatio(
  value: unknown,
  fallback: TileAspectRatio,
): TileAspectRatio {
  if (typeof value === 'string' && (TILE_ASPECT_RATIOS as readonly string[]).includes(value)) {
    return value as TileAspectRatio
  }
  return fallback
}

/** タイル型用：不正値は 1:1 へフォールバック。 */
export function normalizeTileAspectRatio(value: unknown): TileAspectRatio {
  return normalizeAspectRatio(value, DEFAULT_TILE_ASPECT_RATIO)
}

/** スライダー型用：不正値は 9:16 へフォールバック。 */
export function normalizeSliderAspectRatio(value: unknown): TileAspectRatio {
  return normalizeAspectRatio(value, DEFAULT_SLIDER_ASPECT_RATIO)
}

/**
 * インライン style 用の padding-bottom 文字列を返す（埋め込み用 TilePlayer / SliderPlayer など、
 * Tailwind 任意値が使えない箇所向け）。
 * 比率 a:b の縦／横は height/width = b/a なので、padding-bottom = (b/a) * 100%。
 */
export function aspectRatioToPaddingBottom(aspectRatio: TileAspectRatio): string {
  const [a, b] = aspectRatio.split(':').map(Number)
  return `${(b / a) * 100}%`
}

/**
 * Tailwind の aspect-ratio クラス文字列を返す。
 *
 * 🚨 テンプレートリテラルで `aspect-[${ratio}]` と動的生成すると、Tailwind のビルド時
 * スキャンがクラス名を検出できず CSS が出力されない（= プレビューのサムネイルが高さ0で
 * 潰れる）。そのため、全ての候補をリテラル文字列で静的に列挙してスキャン対象にする。
 */
const ASPECT_RATIO_TAILWIND_CLASSES: Record<TileAspectRatio, string> = {
  '1:1': 'aspect-[1/1]',
  '3:4': 'aspect-[3/4]',
  '9:16': 'aspect-[9/16]',
  '4:3': 'aspect-[4/3]',
}

export function aspectRatioToTailwindClass(aspectRatio: TileAspectRatio): string {
  return ASPECT_RATIO_TAILWIND_CLASSES[aspectRatio]
}
